import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { ProgramHighlightModule, parseTopicLine } from '../../utils/parseCourseContent';

interface ProgramHighlightsAccordionProps {
  modules: ProgramHighlightModule[];
  defaultOpenIndex?: number | null;
  variant?: 'default' | 'compact';
}

const ProgramHighlightsAccordion: React.FC<ProgramHighlightsAccordionProps> = ({
  modules,
  defaultOpenIndex = 0,
  variant = 'default',
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(defaultOpenIndex);

  const toggle = (index: number) => {
    setOpenIndex((current) => (current === index ? null : index));
  };

  return (
    <div className={variant === 'compact' ? 'space-y-2' : 'space-y-3'}>
      {modules.map((highlightModule, index) => {
        const isOpen = openIndex === index;
        const padding = variant === 'compact' ? 'px-4 py-3' : 'px-5 py-4';

        return (
          <div
            key={index}
            className={`rounded-2xl border overflow-hidden transition-all duration-200 ${
              isOpen
                ? 'border-primary-200 bg-white shadow-md ring-1 ring-primary-100'
                : 'border-gray-200 bg-white shadow-sm hover:border-primary-200/70 hover:shadow'
            }`}
          >
            <button
              type="button"
              aria-expanded={isOpen}
              onClick={() => toggle(index)}
              className={`w-full flex items-center gap-3 sm:gap-4 text-left ${padding}`}
            >
              <span
                className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl text-sm font-bold transition-colors ${
                  isOpen ? 'bg-primary-600 text-white' : 'bg-primary-50 text-primary-700'
                }`}
              >
                {String(index + 1).padStart(2, '0')}
              </span>

              <span className="flex-1 min-w-0">
                <span className="block font-semibold text-gray-900 leading-snug">
                  {highlightModule.module}
                </span>
              </span>

              <ChevronDown
                className={`h-5 w-5 flex-shrink-0 text-gray-400 transition-transform duration-200 ${
                  isOpen ? 'rotate-180 text-primary-600' : ''
                }`}
              />
            </button>

            <div
              className={`grid transition-all duration-200 ease-in-out ${
                isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
              }`}
            >
              <div className="overflow-hidden">
                <div className={`border-t border-gray-100 ${variant === 'compact' ? 'px-4 pb-4 pt-3' : 'px-5 pb-5 pt-4'}`}>
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
                    {highlightModule.topics.map((topic, topicIndex) => {
                      const { title, description } = parseTopicLine(topic);
                      return (
                        <li key={topicIndex} className="flex gap-2.5 text-sm">
                          <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary-500" />
                          <span className="text-gray-700 leading-relaxed">
                            <span className="font-medium text-gray-900">{title}</span>
                            {description ? (
                              <span className="text-gray-600"> — {description}</span>
                            ) : null}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ProgramHighlightsAccordion;
